class FloatingMegaMenu extends HTMLElement {
  constructor() {
    super();
    this.hideTimeout = null;
    this.isInitialized = false;
    this.boundHandlers = {};
    this.mobileBreakpoint = window.matchMedia('(max-width: 999px)');
    this.sidebarOriginalParents = new Map();
  }

  connectedCallback() {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.triggerHandle = this.dataset.triggerHandle;
    this.sidebarWrapper = this.querySelector('[data-sidebar-wrapper]');
    this.contentWrapper = this.querySelector('[data-content-wrapper]');
    this.mobileTitleEl = this.querySelector('[data-mobile-title]');
    this.backButton = this.querySelector('[data-floating-back]');

    this.snapshotOriginalSidebarParents();
    this.setupDrawerTrigger();
    this.setupBackButton();

    this.applyModeHandler = () => this.applyMode();
    if (this.mobileBreakpoint.addEventListener) {
      this.mobileBreakpoint.addEventListener('change', this.applyModeHandler);
    } else if (this.mobileBreakpoint.addListener) {
      this.mobileBreakpoint.addListener(this.applyModeHandler);
    }

    this.applyMode();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  cleanup() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }

    this.teardownDesktop();
    this.teardownMobile();

    if (this.applyModeHandler) {
      if (this.mobileBreakpoint.removeEventListener) {
        this.mobileBreakpoint.removeEventListener('change', this.applyModeHandler);
      } else if (this.mobileBreakpoint.removeListener) {
        this.mobileBreakpoint.removeListener(this.applyModeHandler);
      }
    }

    if (this.drawerClickHandler) {
      document.removeEventListener('click', this.drawerClickHandler, true);
      this.drawerClickHandler = null;
    }

    if (this.drawerObserver) {
      this.drawerObserver.disconnect();
      this.drawerObserver = null;
    }

    if (this.backButtonHandler && this.backButton) {
      this.backButton.removeEventListener('click', this.backButtonHandler);
      this.backButtonHandler = null;
    }

    this.isInitialized = false;
  }

  snapshotOriginalSidebarParents() {
    this.querySelectorAll('.floating-section').forEach((section) => {
      const sidebar = section.querySelector('.floating-section__sidebar');
      if (sidebar) {
        this.sidebarOriginalParents.set(sidebar, section);
      }
    });
  }

  applyMode() {
    if (this.mobileBreakpoint.matches) {
      this.teardownDesktop();
      this.setupMobile();
    } else {
      this.teardownMobile();
      this.setupDesktop();
    }
  }

  // ---------- Desktop mode ----------

  setupDesktop() {
    if (this.desktopInitialized) return;
    this.desktopInitialized = true;

    this.setupTrigger();
    this.setupMenuEvents();
    this.mountSidebarsInWrapper();
    this.activateFirstSection();
  }

  teardownDesktop() {
    if (!this.desktopInitialized) return;
    this.desktopInitialized = false;

    if (this.trigger && this.boundHandlers.triggerMouseEnter) {
      this.trigger.removeEventListener('mouseenter', this.boundHandlers.triggerMouseEnter);
      this.trigger.removeEventListener('mouseleave', this.boundHandlers.triggerMouseLeave);
      this.trigger.removeEventListener('focusin', this.boundHandlers.triggerFocusIn);
      this.trigger.removeEventListener('focusout', this.boundHandlers.triggerFocusOut);
    }

    if (this.menuMouseEnter) this.removeEventListener('mouseenter', this.menuMouseEnter);
    if (this.menuMouseLeave) this.removeEventListener('mouseleave', this.menuMouseLeave);
    if (this.menuFocusIn) this.removeEventListener('focusin', this.menuFocusIn);
    if (this.menuFocusOut) this.removeEventListener('focusout', this.menuFocusOut);
    this.menuMouseEnter = this.menuMouseLeave = this.menuFocusIn = this.menuFocusOut = null;

    if (this.sidebarMouseOver) this.sidebarWrapper?.removeEventListener('mouseover', this.sidebarMouseOver);
    if (this.sidebarFocusIn) this.sidebarWrapper?.removeEventListener('focusin', this.sidebarFocusIn);
    this.sidebarMouseOver = this.sidebarFocusIn = null;

    this.restoreSidebarsToSections();
    this.hide();
  }

  setupTrigger() {
    this.trigger = document.querySelector(`[data-floating-trigger="${this.triggerHandle}"]`);
    if (!this.trigger) return;

    this.trigger.classList.add('has-floating-mega-menu');

    this.boundHandlers.triggerMouseEnter = () => this.show();
    this.boundHandlers.triggerMouseLeave = () => this.scheduleHide();
    this.boundHandlers.triggerFocusIn = () => this.show();
    this.boundHandlers.triggerFocusOut = (e) => {
      if (!this.trigger.contains(e.relatedTarget) && !this.contains(e.relatedTarget)) {
        this.hide();
      }
    };

    this.trigger.addEventListener('mouseenter', this.boundHandlers.triggerMouseEnter);
    this.trigger.addEventListener('mouseleave', this.boundHandlers.triggerMouseLeave);
    this.trigger.addEventListener('focusin', this.boundHandlers.triggerFocusIn);
    this.trigger.addEventListener('focusout', this.boundHandlers.triggerFocusOut);
  }

  setupMenuEvents() {
    this.menuMouseEnter = () => this.show();
    this.menuMouseLeave = () => this.scheduleHide();
    this.menuFocusIn = () => this.cancelHide();
    this.menuFocusOut = (e) => {
      if (!this.contains(e.relatedTarget) && (!this.trigger || !this.trigger.contains(e.relatedTarget))) {
        this.hide();
      }
    };

    this.addEventListener('mouseenter', this.menuMouseEnter);
    this.addEventListener('mouseleave', this.menuMouseLeave);
    this.addEventListener('focusin', this.menuFocusIn);
    this.addEventListener('focusout', this.menuFocusOut);
  }

  mountSidebarsInWrapper() {
    const sections = this.querySelectorAll('.floating-section');
    if (!sections.length || !this.sidebarWrapper) return;

    sections.forEach((section) => {
      const sidebar = section.querySelector('.floating-section__sidebar');
      if (sidebar && sidebar.parentElement !== this.sidebarWrapper) {
        this.sidebarWrapper.appendChild(sidebar);
      }
    });

    this.sidebarMouseOver = (e) => {
      const trigger = e.target.closest('.floating-section__trigger');
      if (trigger) {
        const sidebar = trigger.closest('.floating-section__sidebar');
        if (sidebar) this.activateSection(sidebar.dataset.sidebarFor);
      }
    };
    this.sidebarFocusIn = (e) => {
      const trigger = e.target.closest('.floating-section__trigger');
      if (trigger) {
        const sidebar = trigger.closest('.floating-section__sidebar');
        if (sidebar) this.activateSection(sidebar.dataset.sidebarFor);
      }
    };

    this.sidebarWrapper.addEventListener('mouseover', this.sidebarMouseOver);
    this.sidebarWrapper.addEventListener('focusin', this.sidebarFocusIn);
  }

  restoreSidebarsToSections() {
    this.sidebarOriginalParents.forEach((section, sidebar) => {
      if (sidebar.parentElement !== section) {
        // Restore as first child so it renders above the content.
        section.insertBefore(sidebar, section.firstChild);
      }
    });
  }

  activateFirstSection() {
    const firstSection = this.querySelector('.floating-section');
    if (firstSection) this.activateSection(firstSection.dataset.sectionId);
  }

  activateSection(sectionId) {
    if (!sectionId) return;

    this.querySelectorAll('.floating-section__sidebar').forEach((s) => s.classList.remove('is-active'));
    this.querySelectorAll('.floating-section').forEach((s) => s.parentElement?.classList.remove('is-active'));

    const targetSidebar = this.querySelector(`[data-sidebar-for="${sectionId}"]`);
    const targetSection = this.querySelector(`.floating-section[data-section-id="${sectionId}"]`);

    if (targetSidebar) targetSidebar.classList.add('is-active');
    if (targetSection) targetSection.parentElement?.classList.add('is-active');
  }

  show() {
    this.cancelHide();
    this.classList.add('is-active');
    if (this.trigger) {
      const link = this.trigger.querySelector('a');
      if (link) link.setAttribute('aria-expanded', 'true');
    }
  }

  hide() {
    this.classList.remove('is-active');
    if (this.trigger) {
      const link = this.trigger.querySelector('a');
      if (link) link.setAttribute('aria-expanded', 'false');
    }
  }

  scheduleHide() {
    this.hideTimeout = setTimeout(() => this.hide(), 100);
  }

  cancelHide() {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = null;
    }
  }

  // ---------- Mobile mode ----------

  setupMobile() {
    if (this.mobileInitialized) return;
    this.mobileInitialized = true;

    this.restoreSidebarsToSections();
    this.applyMobileDefaults();

    this.showAllHandler = (e) => {
      const btn = e.target.closest('[data-show-all]');
      if (!btn || !this.contains(btn)) return;
      e.preventDefault();
      e.stopPropagation();
      const content = btn.closest('.floating-section__content');
      if (content) content.classList.add('has-shown-all');
    };
    this.addEventListener('click', this.showAllHandler);

    this.mobileAccordionHandler = (e) => {
      if (e.target.closest('[data-show-all]')) return;

      const trigger = e.target.closest('.floating-section__trigger');
      if (!trigger || !this.contains(trigger)) return;

      const sidebar = trigger.closest('.floating-section__sidebar');
      if (!sidebar) return;

      // On mobile, always toggle the accordion — even for link triggers.
      // Merchants can surface the link via a "View all" entry inside the
      // expanded content if needed.
      e.preventDefault();
      this.toggleSection(sidebar.dataset.sidebarFor);
    };
    this.addEventListener('click', this.mobileAccordionHandler);
  }

  applyMobileDefaults() {
    this.collapseAllSections();

    const defaultSection = this.querySelector(
      '.floating-section[data-mobile-open-default="true"]'
    );
    if (!defaultSection) return;

    const sidebar = defaultSection.querySelector('.floating-section__sidebar');
    if (sidebar) sidebar.classList.add('is-active');
  }

  teardownMobile() {
    if (!this.mobileInitialized) return;
    this.mobileInitialized = false;

    if (this.mobileAccordionHandler) {
      this.removeEventListener('click', this.mobileAccordionHandler);
      this.mobileAccordionHandler = null;
    }

    if (this.showAllHandler) {
      this.removeEventListener('click', this.showAllHandler);
      this.showAllHandler = null;
    }

    this.closeMobilePanel();
  }

  toggleSection(sectionId) {
    if (!sectionId) return;
    const sidebar = this.querySelector(`[data-sidebar-for="${sectionId}"]`);
    if (!sidebar) return;

    const willOpen = !sidebar.classList.contains('is-active');
    this.querySelectorAll('.floating-section__sidebar').forEach((s) => s.classList.remove('is-active'));
    if (willOpen) sidebar.classList.add('is-active');
  }

  collapseAllSections() {
    this.querySelectorAll('.floating-section__sidebar').forEach((s) => s.classList.remove('is-active'));
  }

  openMobilePanel() {
    if (!this.mobileBreakpoint.matches) return;

    this.updateMobileTitle();
    this.applyMobileDefaults();
    this.classList.add('is-mobile-open');
    document.body.classList.add('overflow-hidden-drawer');
  }

  closeMobilePanel() {
    this.classList.remove('is-mobile-open');
  }

  updateMobileTitle() {
    if (!this.mobileTitleEl) return;

    const drawerLink = this.findDrawerLink();
    let title = '';
    if (drawerLink) {
      const labelEl = drawerLink.querySelector('.menu-drawer__menu-link') || drawerLink;
      title = (labelEl.textContent || '').trim();
    }
    this.mobileTitleEl.textContent = title || this.triggerHandle || '';
  }

  findDrawerLink() {
    if (!this.triggerHandle) return null;
    return document.querySelector(
      `.menu-drawer [data-link-handle="${this.triggerHandle}"]`
    );
  }

  setupBackButton() {
    if (!this.backButton) return;

    this.backButtonHandler = (e) => {
      e.preventDefault();
      this.closeMobilePanel();
    };
    this.backButton.addEventListener('click', this.backButtonHandler);
  }

  // ---------- Drawer integration ----------

  setupDrawerTrigger() {
    const drawerLink = this.findDrawerLink();
    if (drawerLink) {
      drawerLink.classList.add('has-floating-mega-menu');
      this.appendArrowIcon(drawerLink);
    }

    // Use capture phase so we run before the native <details> toggle
    // and before the MenuDrawer's summary click handler.
    this.drawerClickHandler = (e) => {
      if (!this.mobileBreakpoint.matches) return;
      const target = e.target.closest(`[data-link-handle="${this.triggerHandle}"]`);
      if (!target) return;
      if (!target.closest('.menu-drawer')) return;

      e.preventDefault();
      e.stopImmediatePropagation();
      this.openMobilePanel();
    };
    document.addEventListener('click', this.drawerClickHandler, true);

    this.observeDrawerState();
  }

  appendArrowIcon(drawerLink) {
    if (drawerLink.querySelector('.icon-arrow-long')) return;

    const template = this.querySelector('[data-floating-arrow-template]');
    if (template && template.content) {
      drawerLink.appendChild(template.content.cloneNode(true));
    }
  }

  observeDrawerState() {
    const container = document.querySelector('.menu-drawer-container');
    if (!container || this.drawerObserver) return;

    this.drawerObserver = new MutationObserver(() => {
      if (!container.hasAttribute('open') && this.classList.contains('is-mobile-open')) {
        this.closeMobilePanel();
      }
    });
    this.drawerObserver.observe(container, { attributes: true, attributeFilter: ['open'] });
  }
}

if (!customElements.get('floating-mega-menu')) {
  customElements.define('floating-mega-menu', FloatingMegaMenu);
}
